import type { NavigationItem } from "@/components/ui/navigation/types";
import { splitChildrenByVisibility } from "./selectors";

/**
 * Reorders one submenu child within its own visibility group (the up-front list
 * OR the "More" group), then re-flattens to `[...visible, ...hidden]` with a
 * dense 0-based `order`. Reordering is id-based so it stays correct regardless
 * of how the editor indexes the split lists. Other parents are untouched.
 */
export function moveChildInItems(
  items: NavigationItem[],
  parentId: string,
  childId: string,
  direction: "up" | "down"
): NavigationItem[] {
  return items.map((item) => {
    if (item.id !== parentId || !item.children) return item;

    const { visible, hidden } = splitChildrenByVisibility(item.children);
    const group = visible.some((c) => c.id === childId) ? visible : hidden;
    const index = group.findIndex((c) => c.id === childId);
    const target = direction === "up" ? index - 1 : index + 1;

    if (index !== -1 && target >= 0 && target < group.length) {
      [group[index], group[target]] = [group[target], group[index]];
    }

    const reordered = [...visible, ...hidden].map((child, idx) => ({
      ...child,
      order: idx,
    }));
    return { ...item, children: reordered };
  });
}

/**
 * Toggles the visibility of one submenu child. Hidden children (`visible: false`)
 * are collapsed into the dropdown's labeled "More" group rather than removed.
 */
export function toggleChildVisibilityInItems(
  items: NavigationItem[],
  parentId: string,
  childId: string
): NavigationItem[] {
  return items.map((item) => {
    if (item.id !== parentId || !item.children) return item;
    return {
      ...item,
      children: item.children.map((child) =>
        child.id === childId
          ? { ...child, visible: child.visible === false ? true : false }
          : child
      ),
    };
  });
}
